/**
 * Arc and circle geometry for user markups (PDF page space).
 * - circular: center + start + end (constant radius, CAD-style)
 * - bulge: start + end + bulge control (quadratic bezier)
 */

import { pdfPointToCanvas } from './userMarkup.js';

export const ARC_STYLES = {
  CIRCULAR: 'circular',
  BULGE: 'bulge',
};

export function isCircularArc(geometry) {
  if (!geometry || geometry.type !== 'arc') return false;
  if (geometry.arcStyle === ARC_STYLES.CIRCULAR) return true;
  if (geometry.arcStyle === ARC_STYLES.BULGE) return false;
  return geometry.center != null
    && geometry.radius != null
    && geometry.startAngle != null
    && geometry.endAngle != null
    && !geometry.bulgePoint;
}

export function circularArcEndpoints(geometry) {
  const { center, radius, startAngle, endAngle } = geometry;
  if (!center || !radius) return [];
  return [
    [center[0] + radius * Math.cos(startAngle), center[1] + radius * Math.sin(startAngle)],
    [center[0] + radius * Math.cos(endAngle), center[1] + radius * Math.sin(endAngle)],
  ];
}

export function buildCircularArcGeometry(center, startPt, endPt) {
  const radius = Math.hypot(startPt[0] - center[0], startPt[1] - center[1]);
  const startAngle = Math.atan2(startPt[1] - center[1], startPt[0] - center[0]);
  let endAngle = Math.atan2(endPt[1] - center[1], endPt[0] - center[0]);
  if (endAngle < startAngle) endAngle += 2 * Math.PI;
  const points = circularArcEndpoints({ center, radius, startAngle, endAngle });
  return {
    type: 'arc',
    arcStyle: ARC_STYLES.CIRCULAR,
    center,
    radius,
    startAngle,
    endAngle,
    counterClockwise: true,
    points,
  };
}

export function buildBulgeArcGeometry(pdfVertices) {
  if (!pdfVertices || pdfVertices.length < 3) return null;
  return {
    type: 'arc',
    arcStyle: ARC_STYLES.BULGE,
    points: [pdfVertices[0], pdfVertices[1]],
    bulgePoint: pdfVertices[2],
  };
}

/** Migrate legacy arcs stored as 3 collinear polyline points. */
export function normalizeArcGeometry(geometry) {
  if (!geometry || geometry.type !== 'arc') return geometry;

  if (isCircularArc(geometry)) {
    const points = circularArcEndpoints(geometry);
    return {
      ...geometry,
      arcStyle: ARC_STYLES.CIRCULAR,
      points,
    };
  }

  if (geometry.bulgePoint && geometry.points?.length >= 2) {
    return { ...geometry, arcStyle: ARC_STYLES.BULGE };
  }

  if (geometry.points?.length === 3) {
    return {
      ...geometry,
      arcStyle: ARC_STYLES.BULGE,
      points: [geometry.points[0], geometry.points[1]],
      bulgePoint: geometry.points[2],
    };
  }

  return { ...geometry, arcStyle: geometry.arcStyle || ARC_STYLES.BULGE };
}

export function arcPdfVertices(geometry) {
  const geom = normalizeArcGeometry(geometry);
  if (!geom || geom.type !== 'arc') return [];

  if (isCircularArc(geom)) {
    const [start, end] = circularArcEndpoints(geom);
    return [geom.center, start, end];
  }

  const [start, end] = geom.points || [];
  const bulge = geom.bulgePoint;
  if (!start || !end || !bulge) return geom.points || [];
  return [start, end, bulge];
}

export function circlePdfVertices(geometry) {
  if (!geometry || geometry.type !== 'circle') return [];
  const { center, radius } = geometry;
  if (!center || !radius) return [];
  return [center, [center[0] + radius, center[1]]];
}

export function arcCanvasPoints(geometry, viewport) {
  return arcPdfVertices(geometry).map(([px, py]) => pdfPointToCanvas(px, py, viewport));
}

export function sampleCircularArcPdfPoints(geometry, steps = 48) {
  const geom = normalizeArcGeometry(geometry);
  if (!isCircularArc(geom)) return [];
  const { center, radius, startAngle, endAngle } = geom;
  const pts = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const angle = startAngle + t * (endAngle - startAngle);
    pts.push([
      center[0] + radius * Math.cos(angle),
      center[1] + radius * Math.sin(angle),
    ]);
  }
  return pts;
}

function sampleQuadraticBezier(p0, p1, p2, steps = 24) {
  const pts = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const u = 1 - t;
    pts.push({
      x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x,
      y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y,
    });
  }
  return pts;
}

export function quadraticBezierLength(p0, p1, p2, steps = 32) {
  const pts = sampleQuadraticBezier(p0, p1, p2, steps);
  let len = 0;
  for (let i = 1; i < pts.length; i += 1) {
    len += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
  }
  return len;
}

function pointToSegmentDist(px, py, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - a.x, py - a.y);
  let t = ((px - a.x) * dx + (py - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;
  return Math.hypot(px - projX, py - projY);
}

export function paintArcGeometry(ctx, geometry, viewport, {
  stroke = '#e97024',
  width = 2,
  dash = null,
} = {}) {
  const geom = normalizeArcGeometry(geometry);
  if (!geom || !viewport) return;

  ctx.save();
  if (dash) ctx.setLineDash(dash);
  ctx.strokeStyle = stroke;
  ctx.lineWidth = width;
  ctx.beginPath();

  if (isCircularArc(geom)) {
    const samples = sampleCircularArcPdfPoints(geom, 48);
    if (samples.length < 2) {
      ctx.restore();
      return;
    }
    const first = pdfPointToCanvas(samples[0][0], samples[0][1], viewport);
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < samples.length; i += 1) {
      const p = pdfPointToCanvas(samples[i][0], samples[i][1], viewport);
      ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();
    ctx.restore();
    return;
  }

  const verts = arcPdfVertices(geom);
  if (verts.length < 3) {
    ctx.restore();
    return;
  }
  const [s, e, b] = verts.map(([px, py]) => pdfPointToCanvas(px, py, viewport));
  ctx.moveTo(s.x, s.y);
  ctx.quadraticCurveTo(b.x, b.y, e.x, e.y);
  ctx.stroke();
  ctx.restore();
}

export function paintCircleGeometry(ctx, geometry, viewport, {
  stroke = '#e97024',
  fill = null,
  width = 2,
  dash = null,
} = {}) {
  if (!geometry?.center || !geometry.radius || !viewport) return;
  const center = pdfPointToCanvas(geometry.center[0], geometry.center[1], viewport);
  const edge = pdfPointToCanvas(
    geometry.center[0] + geometry.radius,
    geometry.center[1],
    viewport,
  );
  const r = Math.hypot(edge.x - center.x, edge.y - center.y);
  ctx.save();
  if (dash) ctx.setLineDash(dash);
  ctx.beginPath();
  ctx.arc(center.x, center.y, r, 0, Math.PI * 2);
  if (fill) {
    ctx.fillStyle = fill;
    ctx.fill();
  }
  ctx.strokeStyle = stroke;
  ctx.lineWidth = width;
  ctx.stroke();
  ctx.restore();
}

export function arcLengthPdf(geometry) {
  const geom = normalizeArcGeometry(geometry);
  if (isCircularArc(geom)) {
    return geom.radius * Math.abs(geom.endAngle - geom.startAngle);
  }
  const verts = arcPdfVertices(geom);
  if (verts.length < 3) return 0;
  const [s, e, b] = verts;
  return quadraticBezierLength(
    { x: s[0], y: s[1] },
    { x: b[0], y: b[1] },
    { x: e[0], y: e[1] },
  );
}

export function hitTestArc(canvasX, canvasY, geometry, viewport, threshold = 8) {
  const geom = normalizeArcGeometry(geometry);
  if (!viewport) return false;

  if (isCircularArc(geom)) {
    const samples = sampleCircularArcPdfPoints(geom, 32)
      .map(([px, py]) => pdfPointToCanvas(px, py, viewport));
    for (let i = 0; i < samples.length - 1; i += 1) {
      if (pointToSegmentDist(canvasX, canvasY, samples[i], samples[i + 1]) <= threshold) {
        return true;
      }
    }
    return false;
  }

  const canvasPts = arcCanvasPoints(geom, viewport);
  if (canvasPts.length < 3) return false;
  const [s, e, b] = canvasPts;
  const samples = sampleQuadraticBezier(s, b, e, 32);
  for (let i = 0; i < samples.length - 1; i += 1) {
    if (pointToSegmentDist(canvasX, canvasY, samples[i], samples[i + 1]) <= threshold) {
      return true;
    }
  }
  return false;
}

export function hitTestCircle(canvasX, canvasY, geometry, viewport, threshold = 8) {
  if (!geometry?.center || !geometry.radius) return false;
  const center = pdfPointToCanvas(geometry.center[0], geometry.center[1], viewport);
  const edge = pdfPointToCanvas(
    geometry.center[0] + geometry.radius,
    geometry.center[1],
    viewport,
  );
  const r = Math.hypot(edge.x - center.x, edge.y - center.y);
  const dist = Math.hypot(canvasX - center.x, canvasY - center.y);
  return Math.abs(dist - r) <= threshold;
}

export function buildArcGeometryFromVertices(pdfVertices, { arcStyle } = {}) {
  if (!pdfVertices || pdfVertices.length < 3) return null;
  if (arcStyle === ARC_STYLES.CIRCULAR) {
    return buildCircularArcGeometry(pdfVertices[0], pdfVertices[1], pdfVertices[2]);
  }
  return buildBulgeArcGeometry(pdfVertices);
}

export function buildCircleGeometryFromVertices(pdfVertices) {
  if (!pdfVertices || pdfVertices.length < 2) return null;
  const center = pdfVertices[0];
  const radius = Math.hypot(
    pdfVertices[1][0] - center[0],
    pdfVertices[1][1] - center[1],
  );
  return {
    type: 'circle',
    center,
    radius,
    points: [],
  };
}

function projectOntoCircle(center, radius, pt) {
  const dx = pt[0] - center[0];
  const dy = pt[1] - center[1];
  const d = Math.hypot(dx, dy);
  if (d < 1e-6) {
    return [center[0] + radius, center[1]];
  }
  return [center[0] + (radius * dx) / d, center[1] + (radius * dy) / d];
}

export function updateArcVertexAtIndex(geometry, vertexIndex, newPoint) {
  const geom = normalizeArcGeometry(geometry);

  if (isCircularArc(geom)) {
    const { center, radius, startAngle, endAngle } = geom;
    if (vertexIndex === 0) {
      const dx = newPoint[0] - center[0];
      const dy = newPoint[1] - center[1];
      const nc = newPoint;
      return buildCircularArcGeometry(
        nc,
        [nc[0] + radius * Math.cos(startAngle), nc[1] + radius * Math.sin(startAngle)],
        [nc[0] + radius * Math.cos(endAngle), nc[1] + radius * Math.sin(endAngle)],
      );
    }
    if (vertexIndex === 1) {
      const newRadius = Math.hypot(newPoint[0] - center[0], newPoint[1] - center[1]);
      const newStartAngle = Math.atan2(newPoint[1] - center[1], newPoint[0] - center[0]);
      let newEndAngle = endAngle;
      if (newEndAngle < newStartAngle) newEndAngle += 2 * Math.PI;
      return {
        ...geom,
        radius: newRadius || radius,
        startAngle: newStartAngle,
        endAngle: newEndAngle,
        points: circularArcEndpoints({
          center,
          radius: newRadius || radius,
          startAngle: newStartAngle,
          endAngle: newEndAngle,
        }),
      };
    }
    if (vertexIndex === 2) {
      const onCircle = projectOntoCircle(center, radius, newPoint);
      let newEndAngle = Math.atan2(onCircle[1] - center[1], onCircle[0] - center[0]);
      let newStartAngle = startAngle;
      if (newEndAngle < newStartAngle) newEndAngle += 2 * Math.PI;
      return {
        ...geom,
        endAngle: newEndAngle,
        points: circularArcEndpoints({
          center,
          radius,
          startAngle: newStartAngle,
          endAngle: newEndAngle,
        }),
      };
    }
    return geom;
  }

  const [start, end] = geom.points || [];
  if (vertexIndex === 0) {
    return { ...geom, points: [newPoint, end] };
  }
  if (vertexIndex === 1) {
    return { ...geom, points: [start, newPoint] };
  }
  if (vertexIndex === 2) {
    return { ...geom, bulgePoint: newPoint };
  }
  return geom;
}

export function updateCircleVertexAtIndex(geometry, vertexIndex, newPoint) {
  if (!geometry?.center) return geometry;
  if (vertexIndex === 0) {
    return { ...geometry, center: newPoint };
  }
  const radius = Math.hypot(
    newPoint[0] - geometry.center[0],
    newPoint[1] - geometry.center[1],
  );
  return { ...geometry, radius };
}

export function translateArcGeometry(geometry, dx, dy) {
  const geom = normalizeArcGeometry(geometry);
  if (isCircularArc(geom)) {
    const nc = [geom.center[0] + dx, geom.center[1] + dy];
    return {
      ...geom,
      center: nc,
      points: circularArcEndpoints({ ...geom, center: nc }),
    };
  }
  return {
    ...geom,
    points: (geom.points || []).map(([x, y]) => [x + dx, y + dy]),
    bulgePoint: geom.bulgePoint
      ? [geom.bulgePoint[0] + dx, geom.bulgePoint[1] + dy]
      : geom.bulgePoint,
  };
}

export function translateCircleGeometry(geometry, dx, dy) {
  if (!geometry?.center) return geometry;
  return {
    ...geometry,
    center: [geometry.center[0] + dx, geometry.center[1] + dy],
  };
}
